import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { useFolders } from '../hooks/useFolders';
import { apiClient } from '../services/api';
import type { ConversationListItem, Folder } from '../types';

export interface ConversationListWithFoldersProps {
  conversations: ConversationListItem[];
  selectedConversationId?: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onNewConversation: () => void;
  onSearch?: (query: string) => void;
  onConversationMoved?: () => void;
  isLoading?: boolean;
}

export const ConversationListWithFolders: React.FC<ConversationListWithFoldersProps> = ({
  conversations,
  selectedConversationId,
  onSelect,
  onDelete,
  onNewConversation,
  onSearch,
  onConversationMoved,
  isLoading = false,
}) => {
  const { folders, createFolder, deleteFolder, updateFolder } = useFolders();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['uncategorized']));
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState<Folder | null>(null);
  const [folderName, setFolderName] = useState('');
  const [folderToDelete, setFolderToDelete] = useState<Folder | null>(null);
  const [deleteFolderModalOpen, setDeleteFolderModalOpen] = useState(false);
  const [draggedConversation, setDraggedConversation] = useState<string | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    setConversationToDelete(conversationId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (conversationToDelete) {
      onDelete(conversationToDelete);
      setDeleteModalOpen(false);
      setConversationToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setConversationToDelete(null);
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (onSearch) {
      onSearch(query);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    if (onSearch) {
      onSearch('');
    }
  };

  const highlightText = (text: string, query: string): React.ReactNode => {
    if (!query.trim()) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} className="bg-yellow-200 dark:bg-yellow-600 text-gray-900 dark:text-gray-100">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleNewFolder = () => {
    setFolderToEdit(null);
    setFolderName('');
    setFolderModalOpen(true);
  };

  const handleEditFolder = (e: React.MouseEvent, folder: Folder) => {
    e.stopPropagation();
    setFolderToEdit(folder);
    setFolderName(folder.name);
    setFolderModalOpen(true);
  };

  const handleSaveFolder = async () => {
    if (!folderName.trim()) return;

    if (folderToEdit) {
      await updateFolder(folderToEdit.id, folderName);
    } else {
      await createFolder(folderName);
    }

    setFolderModalOpen(false);
    setFolderName('');
    setFolderToEdit(null);
  };

  const handleDeleteFolderClick = (e: React.MouseEvent, folder: Folder) => {
    e.stopPropagation();
    setFolderToDelete(folder);
    setDeleteFolderModalOpen(true);
  };

  const handleConfirmDeleteFolder = async () => {
    if (folderToDelete) {
      await deleteFolder(folderToDelete.id);
      setDeleteFolderModalOpen(false);
      setFolderToDelete(null);
      if (onConversationMoved) {
        onConversationMoved();
      }
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, conversationId: string) => {
    setDraggedConversation(conversationId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetFolderId: string | null) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedConversation) return;

    try {
      await apiClient.updateConversationMetadata(draggedConversation, {
        folderId: targetFolderId,
      });
      
      if (onConversationMoved) {
        onConversationMoved();
      }
    } catch (error) {
      console.error('Error moving conversation:', error);
    } finally {
      setDraggedConversation(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedConversation(null);
  };

  // Group conversations by folder
  const conversationsByFolder = conversations.reduce((acc, conv) => {
    const folderId = conv.folderId || 'uncategorized';
    if (!acc[folderId]) {
      acc[folderId] = [];
    }
    acc[folderId].push(conv);
    return acc;
  }, {} as Record<string, ConversationListItem[]>);

  const conversationToDeleteItem = conversations.find(c => c.id === conversationToDelete);

  const renderConversation = (conversation: ConversationListItem) => (
    <div
      key={conversation.id}
      draggable
      onDragStart={(e) => handleDragStart(e, conversation.id)}
      onDragEnd={handleDragEnd}
      className={`
        relative transition-colors cursor-move
        hover:bg-gray-100 dark:hover:bg-gray-800
        ${selectedConversationId === conversation.id
          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
          : 'border-l-4 border-transparent'
        }
        ${draggedConversation === conversation.id ? 'opacity-50' : ''}
      `}
    >
      <div className="flex items-start justify-between gap-2 px-4 py-3">
        <button
          onClick={() => onSelect(conversation.id)}
          className="flex-1 min-w-0 text-left focus:outline-none"
        >
          <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {searchQuery ? highlightText(truncateText(conversation.title, 50), searchQuery) : truncateText(conversation.title, 50)}
          </h3>
          
          <p className="text-sm text-gray-600 dark:text-gray-400 truncate mt-1">
            {searchQuery ? highlightText(truncateText(conversation.lastMessage, 60), searchQuery) : truncateText(conversation.lastMessage, 60)}
          </p>
          
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-500">
            <span>{formatTimestamp(conversation.lastUpdated)}</span>
            <span>•</span>
            <span>{conversation.messageCount} messages</span>
            {conversation.models.length > 0 && (
              <>
                <span>•</span>
                <span className="truncate">
                  {conversation.models.slice(0, 2).join(', ')}
                  {conversation.models.length > 2 && ` +${conversation.models.length - 2}`}
                </span>
              </>
            )}
          </div>
        </button>

        <button
          onClick={(e) => handleDeleteClick(e, conversation.id)}
          className="shrink-0 p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded"
          aria-label="Delete conversation"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-2">
        <Button onClick={onNewConversation} className="w-full" disabled={isLoading}>
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Conversation
        </Button>
        
        <Button onClick={handleNewFolder} variant="secondary" className="w-full" disabled={isLoading}>
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          New Folder
        </Button>
      </div>

      {/* Search */}
      {onSearch && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search conversations..."
              className="w-full px-4 py-2 pl-10 pr-10 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button onClick={handleClearSearch} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Folders and Conversations */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-gray-500 dark:text-gray-400">Loading...</div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex items-center justify-center h-32 px-4">
            <p className="text-gray-500 dark:text-gray-400 text-center text-sm">
              No conversations yet. Click "New Conversation" to start!
            </p>
          </div>
        ) : (
          <div>
            {/* Uncategorized conversations */}
            {conversationsByFolder['uncategorized'] && conversationsByFolder['uncategorized'].length > 0 && (
              <div className="border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => toggleFolder('uncategorized')}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, null)}
                  className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <svg
                      className={`w-4 h-4 transition-transform ${expandedFolders.has('uncategorized') ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Uncategorized</span>
                    <span className="text-sm text-gray-500">({conversationsByFolder['uncategorized'].length})</span>
                  </div>
                </button>
                {expandedFolders.has('uncategorized') && (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {conversationsByFolder['uncategorized'].map(renderConversation)}
                  </div>
                )}
              </div>
            )}

            {/* User folders */}
            {folders.map(folder => (
              <div key={folder.id} className="border-b border-gray-200 dark:border-gray-700">
                <div
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, folder.id)}
                  className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <button
                    onClick={() => toggleFolder(folder.id)}
                    className="flex-1 flex items-center gap-2 text-left"
                  >
                    <svg
                      className={`w-4 h-4 transition-transform ${expandedFolders.has(folder.id) ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    <span className="font-medium text-gray-700 dark:text-gray-300">{folder.name}</span>
                    <span className="text-sm text-gray-500">
                      ({conversationsByFolder[folder.id]?.length || 0})
                    </span>
                  </button>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => handleEditFolder(e, folder)}
                      className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded"
                      aria-label="Edit folder"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => handleDeleteFolderClick(e, folder)}
                      className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded"
                      aria-label="Delete folder"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                {expandedFolders.has(folder.id) && conversationsByFolder[folder.id] && (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {conversationsByFolder[folder.id].map(renderConversation)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Conversation Modal */}
      <Modal isOpen={deleteModalOpen} onClose={handleCancelDelete} title="Delete Conversation" size="sm">
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Are you sure you want to delete this conversation?
          </p>
          
          {conversationToDeleteItem && (
            <div className="bg-gray-100 dark:bg-gray-800 rounded p-3">
              <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                {conversationToDeleteItem.title}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {conversationToDeleteItem.messageCount} messages
              </p>
            </div>
          )}
          
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This action cannot be undone.
          </p>

          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={handleCancelDelete}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirmDelete}>Delete</Button>
          </div>
        </div>
      </Modal>

      {/* Folder Create/Edit Modal */}
      <Modal
        isOpen={folderModalOpen}
        onClose={() => setFolderModalOpen(false)}
        title={folderToEdit ? 'Edit Folder' : 'New Folder'}
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Folder Name
            </label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Enter folder name"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
              maxLength={100}
            />
          </div>

          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setFolderModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveFolder} disabled={!folderName.trim()}>
              {folderToEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Folder Modal */}
      <Modal
        isOpen={deleteFolderModalOpen}
        onClose={() => setDeleteFolderModalOpen(false)}
        title="Delete Folder"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Are you sure you want to delete this folder?
          </p>
          
          {folderToDelete && (
            <div className="bg-gray-100 dark:bg-gray-800 rounded p-3">
              <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                {folderToDelete.name}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {conversationsByFolder[folderToDelete.id]?.length || 0} conversations
              </p>
            </div>
          )}
          
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Conversations in this folder will be moved to "Uncategorized".
          </p>

          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setDeleteFolderModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirmDeleteFolder}>Delete</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
